<script setup lang="ts">
import PriceDisplay from '~/components/PriceDisplay.vue';

useTitle('plans.title', { t: true })

const apiClient = useAPIClient()
const { plans } = await apiClient.plans()

const state = reactive({
  period: 'monthly'
})

const standard = computed(() => plans.value?.find(p => p.code === 'standard'))
const premium = computed(() => plans.value?.find(p => p.code === 'premium'))

const isYearly = computed(() => state.period === 'yearly')
</script>

<template>
  <div class="plans page">
    <header>
      <page-header :heading="$t('plans.title')" />
    </header>

    <div class="content">
      <div class="period-selector justify-center">
        <button
          @click="state.period = 'monthly'"
          :class="{ selected: !isYearly }"
          class="small button"
        >
          {{ $t('plans.monthly') }}
        </button>
        <button
          @click="state.period = 'yearly'"
          :class="{ selected: isYearly }"
          class="small button"
        >
          {{ $t('plans.yearly') }}
        </button>
      </div>

      <div class="plans">
        <div class="feature">
          <div class="compare">
            <div v-for="plan of [standard, premium]" class="plan">
              <div class="heading">
                <h2>{{ plan?.name }}</h2>

                <template v-if="isYearly">
                  <s class="original">
                    <PriceDisplay :price="plan?.pricing?.monthly.total" />
                  </s>
                  <PriceDisplay :price="plan?.pricing?.yearly.byMonth" />
                  <span> / {{ $t('common.month') }}</span>

                  <div v-if="isYearly" class="yearly-info">
                    <PriceDisplay :price="plan?.pricing?.yearly.total" />
                    <span> / {{ $t('common.year') }}</span>
                    <span class="success small tag">
                      {{ $t('plans.two_months_free') }}
                    </span>
                  </div>
                </template>

                <template v-else>
                  <PriceDisplay :price="plan?.pricing?.monthly.total" />
                  <span> / {{ $t('common.month') }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <hr>

        <div class="servers feature">
          <div class="heading">
            <h2>{{ $t('plans.servers') }}</h2>
          </div>

          <div class="compare">
            <div class="plan">
              <p>
                {{ $t('plans.servers_standard') }}
              </p>
            </div>
            <div class="plan">
              <p>
                {{ $t('plans.servers_premium') }}
              </p>
            </div>
          </div>

          <div class="compare badges">
            <div class="plan">
              <feature-badge :title="$t('plans.servers_badge_standard')" />
            </div>
            <div class="plan">
              <feature-badge :title="$t('plans.servers_badge_standard')" />
              <feature-badge :title="$t('plans.servers_badge_premium')" />
            </div>
          </div>
        </div>

        <hr>

        <div class="metrics feature">
          <div class="heading">
            <h2>{{ $t('plans.performance_metrics') }}</h2>
          </div>

          <div class="compare">
            <div class="plan">
              <p>
                {{ $t('plans.performance_standard_desc') }}
              </p>
            </div>
            <div class="plan">
              <p>
                {{ $t('plans.performance_premium_desc') }}
              </p>
            </div>
          </div>

          <div class="compare badges">
            <div class="plan">
              <feature-badge :title="$t('plans.performance_badge_ping_history')" /> 
            </div>
            <div class="plan">
              <feature-badge :title="$t('plans.performance_badge_ping_history')" />
              <feature-badge :title="$t('plans.performance_badge_average_ping')" />
              <feature-badge :title="$t('plans.performance_badge_packet_loss')" />
            </div>
          </div>
        </div>

        <hr>

        <div class="custom-profiles feature">
          <div class="heading">
            <h2>{{ $t('plans.custom_profiles') }}</h2>
          </div>

          <div class="compare">
            <div class="plan">
              <p>
                {{ $t('plans.custom_profiles_desc', { count: 1 }) }}
              </p>
            </div>
            <div class="plan">
              <p>
                {{ $t('plans.custom_profiles_desc', { count: 5 }) }}
              </p>
            </div>
          </div>
        </div>

        <hr>

        <div class="new-feature-access feature">
          <div class="heading">
            <h2>{{ $t('plans.feature_access') }}</h2>
          </div>

          <div class="compare">
            <div class="plan">
              <p>
                {{ $t('plans.feature_access_standard_desc') }}
              </p>
            </div>
            <div class="plan">
              <p>
                {{ $t('plans.feature_access_premium_desc') }}
              </p>
            </div>
          </div>
        </div>

        <hr>

        <div class="support feature">
          <div class="heading">
            <h2>{{ $t('plans.support') }}</h2>
          </div>

          <div class="compare">
            <div class="plan">
              <p>{{ $t('plans.support_standard_desc') }}</p>
            </div>
            <div class="plan">
              <p>{{ $t('plans.support_premium_desc') }}</p>
            </div>
          </div>

          <div class="compare badges">
            <div class="plan">
              <feature-badge :title="$t('plans.support_badge_standard')" />
            </div>
            <div class="plan">
              <feature-badge :title="$t('plans.support_badge_priority')" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.period-selector {
  button {
    opacity: 0.5;
  }

  button.selected {
    opacity: 1;
  }

  button + button { margin-left: 0.25rem; }
}

.content {
  @media (min-width: $breakpoint-md) {
    width: 80%;
    margin: 0 auto;
  }
}

.original {
  margin-right: 0.75rem;
  opacity: 0.5;
}

.heading {
  text-align: center;

  .yearly-info {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0.5rem;
    font-size: 0.85rem;

    .price {
      margin-right: 0.25rem;
      font-size: 1rem;
    }
    .tag { margin-left: 0.25rem; }
  }
}

.feature {
  .compare + .compare {
    margin-top: 1rem;
  }
  .compare {
    display: flex;
    justify-content: space-between;
    text-align: center;

    .plan {
      flex: 1 1 50%;
    }
  }

  .plan:first-child {
    padding-right: 1.5rem;
  }
  .plan:last-child {
    padding-left: 1.5rem;
  }
}

.feature + .feature {
  margin-top: 3rem;
}

hr {
  margin: 3rem 0;
}

.badges {
  .plan {
    .feature-badge + .feature-badge {
      margin-left: 0.5rem;
    }
  }
}
</style>
